import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Page } from "../components/Page";
import { Dropdown } from "../components/Dropdown";
import { ApiError, api, type AppConfig, type WhoAmI } from "../lib/api";
import { FALLBACK_INTAKE, type IntakeOptions } from "../lib/intake";
import { POLICY_VERSION, PRIVACY_URL, RATER_START, REF_PATTERN, SOURCE_PATTERN, TERMS_URL, introLine, versionFromParam } from "../lib/copy";
import { forgetReferral, recalledReferral, rememberReferral } from "../lib/referral";
import type { TakeView } from "../lib/types";
import { landingTouch } from "../lib/landing";
import { countryOfInternational, countryOptions, defaultCountry, dialCode, flag, tidy, toE164, type CountryCode } from "../lib/phone";

type Fields = "firstName" | "email" | "emailConfirm" | "phone" | "sms" | "occupation" | "age" | "terms";
type Errors = Partial<Record<Fields, string>>;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const PHONE_INVALID = "Check the number and country, or leave it blank.";
export const SMS_NEEDS_PHONE = "Add your phone number above, or untick this.";
export const EMAIL_MISMATCH = "The two email addresses don't match.";

export function validateStart(v: {
  firstName: string;
  email: string;
  emailConfirm: string;
  phone?: string;
  /** The country a phone number without a + is read in. */
  phoneCountry?: CountryCode;
  /** Ticked the texts-and-calls box (needs a phone). */
  sms?: boolean;
  occupation?: string;
  age: boolean;
  terms: boolean;
}): Errors {
  const e: Errors = {};
  if (!v.firstName.trim()) e.firstName = "Enter your first name.";
  else if (v.firstName.trim().length > 50) e.firstName = "Use 50 characters or fewer.";
  if (!v.email.trim()) e.email = "Enter your email address.";
  else if (!EMAIL.test(v.email.trim())) e.email = "Enter a valid email address.";
  if (!v.emailConfirm.trim()) e.emailConfirm = "Type your email again.";
  else if (v.email.trim().toLowerCase() !== v.emailConfirm.trim().toLowerCase()) e.emailConfirm = EMAIL_MISMATCH;
  // Optional, but a real number when given: checked against the country's numbering plan.
  const phone = (v.phone ?? "").trim();
  if (phone && !toE164(phone, v.phoneCountry ?? "US")) e.phone = PHONE_INVALID;
  if (v.sms && !phone) e.sms = SMS_NEEDS_PHONE;
  if (!v.occupation) e.occupation = "Choose the one that best describes you.";
  if (!v.age) e.age = "You need to be 18 or older to take the profile.";
  if (!v.terms) e.terms = "Please agree to the Terms and Privacy Policy.";
  return e;
}

const ORDER: Fields[] = ["firstName", "email", "emailConfirm", "phone", "sms", "occupation", "age", "terms"];

export function Start() {
  const [params] = useSearchParams();
  const [current, setCurrent] = useState<TakeView | null | undefined>(undefined);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .currentTake()
      .then((t) => live && setCurrent(t))
      .catch(() => live && setCurrent(null));
    return () => {
      live = false;
    };
  }, []);

  const teamParam = params.get("team") ?? undefined;
  const srcParam = params.get("src");
  const source = srcParam && SOURCE_PATTERN.test(srcParam) ? srcParam : undefined;
  // /?v=4 opts in to another scoring version; anything that isn't one to three digits is ignored.
  const scoringVersion = versionFromParam(params.get("v"));
  // A rater who finished describing someone arrives with ?ref=<their link code>. It's kept on
  // this device, so starting later (without the ?ref) still credits whoever referred them.
  const refParam = params.get("ref");
  const fromLink = refParam && REF_PATTERN.test(refParam) ? refParam : undefined;
  const [referral] = useState(() => fromLink ?? recalledReferral(REF_PATTERN));
  useEffect(() => {
    if (fromLink) rememberReferral(fromLink);
  }, [fromLink]);

  const resume = current && !showForm ? current : null;

  return (
    <Page brand="center">
      {referral ? <StartIntro scoringVersion={scoringVersion} kicker={RATER_START.kicker} message={RATER_START.message} /> : <StartIntro scoringVersion={scoringVersion} />}
      <SignedInLine />

      {current === undefined ? (
        <p role="status" className="mt-10 text-muted">
          Loading…
        </p>
      ) : resume && resume.status === "in_progress" ? (
        <ResumeCard take={resume} onStartOver={() => setShowForm(true)} />
      ) : resume && resume.status === "completed" ? (
        <section className="mt-10 rounded-[16px] border border-line bg-panel p-5" aria-labelledby="done-title">
          <h2 id="done-title" className="heading text-[24px] leading-tight">
            Welcome back, {resume.firstName}. Your profile is ready.
          </h2>
          <div className="mt-5 flex flex-col gap-3">
            <Link to={`/results/${resume.id}`} className="btn btn-primary w-full no-underline">
              See my results
            </Link>
            <button type="button" className="btn btn-secondary w-full" onClick={() => setShowForm(true)}>
              Start a new one
            </button>
          </div>
        </section>
      ) : (
        <StartForm
          source={source}
          referral={referral}
          team={teamParam}
          teamAgreed={params.get("agreed") === "1"}
          scoringVersion={scoringVersion}
        />
      )}
    </Page>
  );
}

/** "Already taken it? Sign in", or, when signed in, a way to their profiles. */
function SignedInLine() {
  const [who, setWho] = useState<WhoAmI | null>(null);
  useEffect(() => {
    let live = true;
    api
      .whoAmI()
      .then((w) => live && setWho(w))
      .catch(() => live && setWho({ signedIn: false, email: null, firstName: null }));
    return () => {
      live = false;
    };
  }, []);
  if (!who) return null;
  return (
    <p className="mt-3 text-center text-[14px] text-muted" data-testid="signed-in-line">
      {who.signedIn ? (
        <>
          Signed in{who.email ? ` as ${who.email}` : ""}. <Link to="/me">See your profiles</Link>
        </>
      ) : (
        <>
          Already taken it? <Link to="/sign-in">Sign in to see your results</Link>
        </>
      )}
    </p>
  );
}

/** Heading block of the start screen. Event links add a kicker (the event name) and a message. */
export function StartIntro({ kicker, message, scoringVersion }: { kicker?: string; message?: string | null; scoringVersion?: number }) {
  return (
    <div className="mt-6 text-center">
      {/* The logo above is the name: the heading is for screen readers. */}
      <h1 className="sr-only">Gifts Profile</h1>
      {kicker ? <p className="eyebrow mb-2 text-accent-text">{kicker}</p> : null}
      <p className="text-[18px] font-semibold leading-snug">Freedom &amp; Clarity · Domains · Gifts</p>
      <p className="mt-1 text-[15px] text-muted">{introLine(scoringVersion)}</p>
      {message ? <p className="mx-auto mt-4 max-w-[400px] text-[16px] leading-snug">{message}</p> : null}
    </div>
  );
}

/** `onStartOver` left out: no starting again (a book code is one test). */
export function ResumeCard({ take, onStartOver }: { take: TakeView; onStartOver?: () => void }) {
  const navigate = useNavigate();
  return (
    <section className="mt-10 rounded-[16px] border border-line bg-panel p-5" aria-labelledby="resume-title">
      <h2 id="resume-title" className="heading text-[24px] leading-tight">
        Welcome back, {take.firstName}. Pick up where you left off?
      </h2>
      <p className="mt-2 text-[15px] text-muted">
        {take.answeredCount} of {take.totalCount} answered.
      </p>
      <div className="mt-5 flex flex-col gap-3">
        <button type="button" className="btn btn-primary w-full" onClick={() => navigate(`/take/${take.id}`)}>
          Continue
        </button>
        {onStartOver ? (
          <button type="button" className="btn btn-secondary w-full" onClick={onStartOver}>
            Start over
          </button>
        ) : null}
      </div>
    </section>
  );
}

export function StartForm({
  source,
  referral,
  campaign,
  team,
  teamAgreed = false,
  scoringVersion,
  personalLink,
  prefill,
}: {
  source?: string;
  referral?: string;
  campaign?: string;
  /** A personal link's token (/i): the take gets the link's price and must use its email. */
  personalLink?: string;
  /** Details a personal link fills in. Its email is fixed: shown, not typed. */
  prefill?: { firstName: string; email: string; phone: string | null };
  /** A team join code: the take is shared with that team (with consent). */
  team?: string;
  teamAgreed?: boolean;
  /** Start this scoring version instead of the default (from ?v=). */
  scoringVersion?: number;
}) {
  const navigate = useNavigate();
  const [prefilledPhone] = useState(() => (prefill?.phone ? tidy(prefill.phone, defaultCountry()) : null));
  const [values, setValues] = useState({
    firstName: prefill?.firstName ?? "",
    email: prefill?.email ?? "",
    emailConfirm: prefill?.email ?? "",
    phone: prefilledPhone?.text ?? "",
    phoneCountry: prefilledPhone?.country ?? defaultCountry(),
    // Never pre-ticked, even on a personal link: consent to texts is their own tick.
    sms: false,
    occupation: "",
    ageBracket: "",
    gender: "",
    age: false,
    terms: false,
    novos: false,
  });
  const [intake, setIntake] = useState<IntakeOptions>(FALLBACK_INTAKE);
  // The texts-and-calls box, word for word from the API (no box until it's loaded: the wording is the record).
  // Where they came from, if they opened the app itself with URL parameters (lib/landing.ts).
  const [landed] = useState(landingTouch);
  const [smsWording, setSmsWording] = useState<NonNullable<AppConfig["smsConsent"]> | null>(null);
  // Reports: this form was opened (once per showing). What it was opened from, and where they came from.
  const viewed = useRef(false);
  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    const kind = campaign ? "event" : referral ? "rater" : team ? "team" : personalLink ? "personal" : "direct";
    // Once per tab for the same form: a reload isn't another opening.
    const seen = `gp_viewed:${kind}:${campaign ?? ""}:${referral ?? ""}`;
    try {
      if (sessionStorage.getItem(seen)) return;
      sessionStorage.setItem(seen, "1");
    } catch {
      // No storage: counted each time.
    }
    api.startViewed({ kind, ...(campaign ? { campaign } : {}), ...(referral ? { ref: referral } : {}), ...(landed ? { touch: landed as Record<string, string> } : {}) });
  }, [campaign, referral, team, personalLink, landed]);
  useEffect(() => {
    api
      .getConfig()
      .then((c) => {
        if (c.intake) setIntake(c.intake);
        if (c.smsConsent) setSmsWording(c.smsConsent);
      })
      .catch(() => undefined);
  }, []);
  const [teamInfo, setTeamInfo] = useState<{ name: string; consent: string } | null>(null);
  const [teamConsent, setTeamConsent] = useState(teamAgreed);
  useEffect(() => {
    if (team) api.teamInvite(team).then(setTeamInfo).catch(() => setTeamInfo(null));
  }, [team]);
  const [errors, setErrors] = useState<Errors>({});
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const refs = useRef<Partial<Record<Fields, HTMLInputElement | null>>>({});

  const update = <K extends keyof typeof values>(k: K, v: (typeof values)[K]) => {
    const next = { ...values, [k]: v };
    setValues(next);
    if (submitted) setErrors(validateStart(next));
  };

  const checkConfirmOnBlur = () => {
    if (values.emailConfirm && values.email && values.email.trim().toLowerCase() !== values.emailConfirm.trim().toLowerCase()) {
      setErrors((e) => ({ ...e, emailConfirm: EMAIL_MISMATCH }));
    } else if (!submitted) {
      setErrors((e) => ({ ...e, emailConfirm: undefined }));
    }
  };

  const onSubmit = async (ev: FormEvent) => {
    ev.preventDefault();
    setSubmitted(true);
    setFormError(null);
    const found = validateStart(values);
    setErrors(found);
    const first = ORDER.find((k) => found[k]);
    if (first) {
      refs.current[first]?.focus();
      return;
    }
    if (teamInfo && !teamConsent) {
      setFormError(`Agree to share your results with ${teamInfo.name} to take it for the team.`);
      return;
    }
    setBusy(true);
    try {
      const { take } = await api.startTake({
        firstName: values.firstName.trim(),
        email: values.email.trim(),
        emailConfirm: values.emailConfirm.trim(),
        ageConfirmed: true,
        policyVersion: POLICY_VERSION,
        novosEmail: values.novos,
        occupation: values.occupation,
        ...(values.ageBracket ? { ageBracket: values.ageBracket } : {}),
        ...(values.gender ? { gender: values.gender } : {}),
        // Sent (and saved) as E.164: +12265551234.
        ...(values.phone.trim() ? { phone: toE164(values.phone, values.phoneCountry)! } : {}),
        ...(values.sms && smsWording ? { smsConsent: true as const, smsConsentVersion: smsWording.version } : {}),
        ...(landed ? { touch: landed } : {}),
        ...(source ? { source } : {}),
        ...(referral ? { ref: referral } : {}),
        ...(campaign ? { campaign } : {}),
        ...(personalLink ? { personalLink } : {}),
        ...(teamInfo && team ? { team, teamConsent: true as const } : {}),
        ...(scoringVersion !== undefined ? { scoringVersion } : {}),
      });
      if (referral) forgetReferral();
      navigate(`/take/${take.id}`);
    } catch (e) {
      setBusy(false);
      if (e instanceof ApiError && (e.code.startsWith("link_") || e.code === "unknown_version" || e.code === "code_test_started")) {
        // link_full / link_closed / link_holding / unknown_version, and a book code whose one test
        // is already started (code_test_started): the API sends a ready-to-show message.
        setFormError(e.message);
      } else if (e instanceof ApiError && e.code === "sms_needs_phone") {
        setErrors((x) => ({ ...x, sms: SMS_NEEDS_PHONE }));
        refs.current.phone?.focus();
      } else if (e instanceof ApiError && e.code === "sms_consent_outdated") {
        setFormError(e.message);
      } else if (e instanceof ApiError && e.code === "invalid_phone") {
        setErrors((x) => ({ ...x, phone: PHONE_INVALID }));
        refs.current.phone?.focus();
      } else if (e instanceof ApiError && e.code === "email_mismatch") {
        setErrors((x) => ({ ...x, emailConfirm: EMAIL_MISMATCH }));
        refs.current.emailConfirm?.focus();
      } else if (e instanceof ApiError && e.status === 0) {
        setFormError("We couldn't reach the server. Check your connection and try again.");
      } else {
        setFormError("We couldn't start your profile. Check your details and try again.");
      }
    }
  };

  return (
    <form className="mt-8 flex flex-col gap-4" onSubmit={onSubmit} noValidate>
      <TextField
        label="First name"
        autoComplete="given-name"
        value={values.firstName}
        onChange={(v) => update("firstName", v)}
        error={errors.firstName}
        inputRef={(el) => (refs.current.firstName = el)}
      />
      {prefill ? (
        // A personal link is for one address: shown, not typed (the API only takes this one).
        <div>
          <p className="mb-1.5 text-[15px] font-semibold">Email</p>
          <p className="field-input flex items-center bg-soft text-muted [overflow-wrap:anywhere]" data-testid="link-email">
            {/* A long address wraps after the @, not in the middle of a word. */}
            <span>
              {prefill.email.split("@")[0]}@<wbr />
              {prefill.email.split("@").slice(1).join("@")}
            </span>
          </p>
          <p className="mt-1.5 text-[14px] text-muted">Your results go here. This link is just for this address.</p>
        </div>
      ) : (
        <>
          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            value={values.email}
            onChange={(v) => update("email", v)}
            error={errors.email}
            inputRef={(el) => (refs.current.email = el)}
          />
          <TextField
            label="Type your email again"
            type="email"
            autoComplete="off"
            value={values.emailConfirm}
            onChange={(v) => update("emailConfirm", v)}
            onBlur={checkConfirmOnBlur}
            error={errors.emailConfirm}
            inputRef={(el) => (refs.current.emailConfirm = el)}
          />
        </>
      )}
      <PhoneField
        value={values.phone}
        country={values.phoneCountry}
        onChange={(phone, phoneCountry) => {
          const next = { ...values, phone, phoneCountry };
          setValues(next);
          if (submitted) setErrors(validateStart(next));
        }}
        error={errors.phone}
        inputRef={(el) => (refs.current.phone = el)}
      />
      {smsWording ? (
        <CheckField
          checked={values.sms}
          onChange={(v) => update("sms", v)}
          error={errors.sms}
          inputRef={(el) => (refs.current.sms = el)}
          ariaLabel={smsWording.label}
        >
          <span className="font-semibold">{smsWording.label}</span>
          <span className="mt-1 block text-[13px] leading-[1.45] text-muted">
            <SmsDisclosure text={smsWording.disclosure} />
          </span>
        </CheckField>
      ) : null}

      <fieldset className="mt-2 flex min-w-0 flex-col gap-4 border-t border-line pt-6">
        <legend className="pr-3 text-[16px] font-bold">About you</legend>
        <div>
          <Dropdown
            label="Which best describes you?"
            value={values.occupation}
            options={intake.occupations}
            onChange={(v) => update("occupation", v)}
          />
          {errors.occupation ? (
            <p className="mt-1.5 text-[14px] text-danger" role="alert">
              {errors.occupation}
            </p>
          ) : null}
        </div>
        {/* Two short answers: side by side. */}
        <div className="grid min-w-0 grid-cols-2 gap-3">
          <Dropdown
            label="Age (optional)"
            value={values.ageBracket}
            options={intake.ageBrackets}
            onChange={(v) => update("ageBracket", v)}
            placeholder="Skip"
          />
          <Dropdown
            label="Gender (optional)"
            value={values.gender}
            options={intake.genders}
            onChange={(v) => update("gender", v)}
            placeholder="Skip"
          />
        </div>
      </fieldset>

      <div className="mt-2 flex flex-col gap-1">
        <CheckField
          checked={values.age}
          onChange={(v) => update("age", v)}
          error={errors.age}
          inputRef={(el) => (refs.current.age = el)}
        >
          I'm 18 or older
        </CheckField>
        <CheckField
          checked={values.terms}
          onChange={(v) => update("terms", v)}
          error={errors.terms}
          inputRef={(el) => (refs.current.terms = el)}
          ariaLabel="I agree to the Terms and Privacy Policy"
        >
          I agree to the{" "}
          <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">
            Terms
          </a>{" "}
          and{" "}
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
        </CheckField>
        <CheckField checked={values.novos} onChange={(v) => update("novos", v)}>
          Send me occasional emails from Novos
        </CheckField>
        {teamInfo ? (
          <CheckField checked={teamConsent} onChange={setTeamConsent}>
            <strong>{teamInfo.name}:</strong> {teamInfo.consent}
          </CheckField>
        ) : null}
      </div>

      {formError ? (
        <p role="alert" className="text-[15px] text-danger">
          {formError}
        </p>
      ) : null}

      <button type="submit" className="btn btn-primary w-full" disabled={busy}>
        {busy ? "Starting…" : "Start"}
      </button>
    </form>
  );
}

/** The small print, with {terms} and {privacy} as the links. */
function SmsDisclosure({ text }: { text: string }) {
  return (
    <>
      {text.split(/(\{terms\}|\{privacy\})/).map((part, i) =>
        part === "{terms}" ? (
          <a key={i} href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="whitespace-nowrap">
            Terms
          </a>
        ) : part === "{privacy}" ? (
          <a key={i} href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="whitespace-nowrap">
            Privacy Policy
          </a>
        ) : (
          part
        ),
      )}
    </>
  );
}

/**
 * Phone with its country code: a compact picker (flag and +code, a native select underneath, so
 * phones get their own list) and the number. Typing a + number picks its country; leaving the
 * field shows a valid number in that country's local style.
 */
function PhoneField({
  value,
  country,
  onChange,
  error,
  inputRef,
}: {
  value: string;
  country: CountryCode;
  onChange: (text: string, country: CountryCode) => void;
  error?: string;
  inputRef: (el: HTMLInputElement | null) => void;
}) {
  const id = useId();
  const [options] = useState(() => countryOptions(country));
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-[14px] font-semibold">
        Phone (optional)
      </label>
      <div className="flex gap-2">
        <div className="relative shrink-0">
          <select
            aria-label="Country code"
            value={country}
            onChange={(e) => onChange(value, e.target.value as CountryCode)}
            className="peer absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
          >
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <span
            aria-hidden="true"
            className="field-input pointer-events-none flex !w-auto items-center gap-1.5 !pr-3 !pl-3.5 tabular-nums peer-focus-visible:outline-2 peer-focus-visible:outline-offset-3 peer-focus-visible:outline-accent"
          >
            <span className="text-[18px] leading-none">{flag(country)}</span>
            <span>{dialCode(country)}</span>
            <svg width="12" height="12" viewBox="0 0 12 12" className="text-muted" aria-hidden="true">
              <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </div>
        <input
          id={id}
          ref={inputRef}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          className="field-input min-w-0 flex-1"
          value={value}
          onChange={(e) => onChange(e.target.value, countryOfInternational(e.target.value) ?? country)}
          onBlur={() => {
            const t = tidy(value, country);
            if (t && (t.text !== value || t.country !== country)) onChange(t.text, t.country);
          }}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
        />
      </div>
      {error ? (
        <p id={`${id}-error`} className="mt-1.5 text-[14px] text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  onBlur,
  error,
  type = "text",
  autoComplete,
  inputRef,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  error?: string;
  type?: string;
  autoComplete?: string;
  inputRef: (el: HTMLInputElement | null) => void;
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-[14px] font-semibold">
        {label}
      </label>
      <input
        id={id}
        ref={inputRef}
        type={type}
        autoComplete={autoComplete}
        className="field-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        {...(type === "email" ? { inputMode: "email" as const, autoCapitalize: "off", spellCheck: false } : {})}
      />
      {error ? (
        <p id={`${id}-error`} className="mt-1.5 text-[14px] text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function CheckField({
  checked,
  onChange,
  error,
  children,
  inputRef,
  ariaLabel,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  error?: string;
  children: ReactNode;
  inputRef?: (el: HTMLInputElement | null) => void;
  /** Full name for the checkbox when the label holds links. */
  ariaLabel?: string;
}) {
  const id = useId();
  return (
    <div>
      <div className="flex min-h-11 items-start gap-3 py-1">
        <input
          id={id}
          ref={inputRef}
          type="checkbox"
          className="mt-0.5 h-6 w-6 shrink-0 accent-accent-text"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          aria-label={ariaLabel}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
        />
        <label htmlFor={id} className="text-[16px] leading-snug">
          {children}
        </label>
      </div>
      {error ? (
        <p id={`${id}-error`} className="ml-9 text-[14px] text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}
